import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-shopify-utils/local-storage-tools";
import { REDO_CART_STORAGE_KEY } from "@redotech/redo-storefront-common/cache/keys";
import { getSessionStorageWithExpiry } from "@redotech/redo-web/utils/session-storage-wrapper";
import type {
  ShopifyCart,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { StorefrontTrackingConfig } from "../config";
import type {
  CheckoutCompletedEventData,
  StorefrontTrackingEvent,
  StorefrontTrackingEventCallback,
  StorefrontTrackingEventType,
} from "../storefront-tracking-event";
import {
  AsyncStorage,
  StorefrontEventsProvider,
} from "./storefront-events-provider";

const MAX_EVENTS_BUFFER_SIZE = 100;
const COMPLETE_CHECKOUT_EVENT_SELECTORS = [
  "#checkout-main .submit-payment",
].join(", ");
const ORDER_CONFIRMATION_PAGE_PATHS = [
  "/checkout/order-confirmation",
  "/order-confirmation",
];

export class BasicEventsProvider implements StorefrontEventsProvider {
  callbacks: Map<
    StorefrontTrackingEventType,
    StorefrontTrackingEventCallback[]
  > = new Map();
  events: StorefrontTrackingEvent[] = [];
  private checkoutButtonListener: ((event: Event) => void) | null = null;

  constructor(config: StorefrontTrackingConfig) {
    this.initEventListeners();
  }

  private initEventListeners() {
    this.emitEvent({
      type: "page_viewed",
      data: {
        url: window.location.href,
        title: document.title,
        timestamp: new Date().toISOString(),
      },
    });
    this.setupCheckoutButtonListener();
    void this.checkForCheckoutPages();
  }

  private convertCartToCheckoutData(
    cart: ShopifyCart,
  ): CheckoutCompletedEventData {
    const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;
    const itemSubtotals =
      cart.items.reduce((acc: number, item: ShopifyCartItem) => {
        acc += item.line_price ?? 0;
        return acc;
      }, 0) / (onTapcart ? 1 : 100); // :(

    return {
      checkout: {
        totalPrice: {
          amount: cart.total_price ?? 0,
          currency: cart.currency || "USD",
        },
        subtotalPrice: {
          amount: cart.subtotal_price || itemSubtotals || 0,
          currency: cart.currency || "USD",
        },
        shippingAddress: undefined,
        lineItems: cart.items.map((item: ShopifyCartItem) => ({
          quantity: item.quantity,
          variant: {
            id: String(item.variant_id),
            product: {
              id: String(item.product_id),
              vendor: item.vendor || "",
            },
          },
        })),
      },
    };
  }

  private async getCartFromCache(): Promise<ShopifyCart | null> {
    // Will only work if the cart is stored in session storage already by the storefront extension
    const cart = await getSessionStorageWithExpiry(REDO_CART_STORAGE_KEY);
    if (cart && typeof cart === "object" && "items" in cart) {
      return cart as unknown as ShopifyCart;
    }

    return null;
  }

  private async checkForCheckoutPages() {
    if (
      ORDER_CONFIRMATION_PAGE_PATHS.some((path) =>
        window.location.pathname.includes(path),
      )
    ) {
      if (
        await getLocalStorageWithExpiry(
          "redo_basic_event_provider_checkout_completed",
          this.storage,
        )
      ) {
        return;
      }
      await setLocalStorageWithExpiry(
        "redo_basic_event_provider_checkout_completed",
        true,
        0.25,
        this.storage,
      );
      const cachedCart = await this.getCartFromCache();
      if (cachedCart) {
        const checkoutData = this.convertCartToCheckoutData(cachedCart);
        this.emitEvent({ type: "checkout_completed", data: checkoutData });
      } else {
        // Fallback to basic data if cart not available
        this.emitEvent({
          type: "checkout_completed",
          data: {
            url: window.location.href,
            title: document.title,
            timestamp: new Date().toISOString(),
            source: "page_load",
          } as unknown as CheckoutCompletedEventData,
        });
      }
    }
  }

  private setupCheckoutButtonListener() {
    if (this.checkoutButtonListener) {
      document.body.removeEventListener(
        "click",
        this.checkoutButtonListener,
        true,
      );
    }

    this.checkoutButtonListener = async (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      if (target.matches && target.matches(COMPLETE_CHECKOUT_EVENT_SELECTORS)) {
        if (
          await getLocalStorageWithExpiry(
            "redo_basic_event_provider_checkout_completed",
            this.storage,
          )
        ) {
          return;
        }
        await setLocalStorageWithExpiry(
          "redo_basic_event_provider_checkout_completed",
          true,
          0.25,
          this.storage,
        );
        const cachedCart = await this.getCartFromCache();
        if (cachedCart) {
          const checkoutData = this.convertCartToCheckoutData(cachedCart);
          this.emitEvent({ type: "checkout_completed", data: checkoutData });
        } else {
          this.emitEvent({
            type: "checkout_completed",
            data: {
              url: window.location.href,
              title: document.title,
              timestamp: new Date().toISOString(),
              source: "button_click",
            } as unknown as CheckoutCompletedEventData,
          });
        }
        return;
      }

      const parentMatch = target.closest(COMPLETE_CHECKOUT_EVENT_SELECTORS);
      if (parentMatch) {
        const cachedCart = await this.getCartFromCache();
        if (cachedCart) {
          const checkoutData = this.convertCartToCheckoutData(cachedCart);
          this.emitEvent({ type: "checkout_completed", data: checkoutData });
        } else {
          this.emitEvent({
            type: "checkout_completed",
            data: {
              url: window.location.href,
              title: document.title,
              timestamp: new Date().toISOString(),
              source: "button_click",
            } as unknown as CheckoutCompletedEventData,
          });
        }
        return;
      }
    };

    document.body.addEventListener("click", this.checkoutButtonListener, {
      capture: true,
      passive: true,
    });
  }

  private emitEvent(event: StorefrontTrackingEvent) {
    if (this.callbacks.has(event.type)) {
      for (const callback of this.callbacks.get(event.type) || []) {
        void callback(event);
      }
    }
    this.events.push(event);
    if (this.events.length > MAX_EVENTS_BUFFER_SIZE) {
      this.events.shift();
    }
  }

  subscribe(
    event: StorefrontTrackingEventType,
    callback: StorefrontTrackingEventCallback,
  ) {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, []);
    }
    this.callbacks.get(event)!.push(callback);

    // When new callback is added, catch up on all pre-existing events -- useful because page view events are emitted before callbacks may be attached, but we don't want to lose those.
    const eventsToCatchUpOn = this.events.filter((e) => e.type === event);
    setTimeout(() => {
      for (const eventToCatchUp of eventsToCatchUpOn) {
        void callback(eventToCatchUp);
      }
    }, 0);
  }

  storage: AsyncStorage = {
    clear: () => Promise.resolve(localStorage.clear()),
    getItem: (key: string) => {
      return Promise.resolve(localStorage.getItem(key));
    },
    key: (index: number) => Promise.resolve(localStorage.key(index)),
    length: () => Promise.resolve(localStorage.length),
    removeItem: (key: string) => Promise.resolve(localStorage.removeItem(key)),
    setItem: (key: string, value: any) => {
      return Promise.resolve(localStorage.setItem(key, value));
    },
  };
}
